import React, { Component } from 'react';
import { connect } from 'react-redux';
import MANAGED_UX_FORMS_CONTROLLER from '../../controllers/managedUxForms';
import LanguageStringResolver from '../fields/language/LanguageStringResolver';
import ApplicationMessages from '../inAppMessages/ApplicationMessages';
import DocumentItem from './DocumentItemV2';
import _ from 'underscore';
import DocumentDetails from './DocumentDetails';
import NoData from '../page/NoData';
import Logger, { LOG_LEVELS, ALL_MESSAGES } from '../../utils/logger';
import ReadOnlyWrapper from '../authorization/ReadOnlyWrapper';

const FORM_NAME = 'PROCESSED_DOCUMENT_LIST_FORM';
const log = new Logger('PROCESSED_DOCUMENT_LIST_FORM', LOG_LEVELS.DEBUG);

class DocumentListForm extends Component {

    constructor(props) {
        super(props);
        this.state = {
            documentDetailsOpen: false,
            expandedRows: {}  // Track which rows are expanded
        };

        // Bind methods
        this.toggleRowExpanded = this.toggleRowExpanded.bind(this);
        this.isRowExpanded = this.isRowExpanded.bind(this);
        this.renderChildRows = this.renderChildRows.bind(this);
    }

    // Toggle expanded state for a row
    toggleRowExpanded(documentId) {
        return () => {
            log.debug('toggleRowExpanded(): ', documentId);
            this.setState(prevState => {
                const expandedRows = { ...prevState.expandedRows };
                expandedRows[documentId] = !expandedRows[documentId];
                return { expandedRows };
            });
        };
    }

    // Check if a row is expanded
    isRowExpanded(documentId) {
        return !!this.state.expandedRows[documentId];
    }

    // Render child rows for a parent document
    renderChildRows(document) {
        if (!document.childRows || !this.isRowExpanded(document.id)) {
            return null;
        }

        const fieldValues = this.props.formData && this.props.formData.fieldValues;

        return (
            <React.Fragment>
                {document.childRows.map((childDoc, childIndex) => (
                    <DocumentItem
                        key={`child-${document.id}-${childIndex}`}
                        formInitialising={!fieldValues}
                        document={childDoc}
                        isChildRow={true}
                        onSelect={this.onSelectDocumentFactory(childDoc.id).bind(this)}
                        selected={fieldValues && fieldValues[childDoc.id + '']}
                        loadDocumentDetails={this.onSelectDocumentDetailFactory(childDoc.id).bind(this)}
                    />
                ))}
            </React.Fragment>
        );
    }

    onSelectAll(select) {
        const documentList = this.props.documentList;
        log.debug('onSelectAll(): ', select, documentList);
        if (!documentList) {
            return;
        }
        const fieldValues = documentList.reduce((values, document) => {
            if (document.id === null) {
                return values;
            }
            values[document.id + ''] = select;

            // Also select/deselect all child documents
            if (document.childRows) {
                document.childRows.forEach(childDoc => {
                    if (childDoc.id !== null) {
                        values[childDoc.id + ''] = select;
                    }
                });
            }

            return values;
        }, { '_all': select });
        this.props.doInitialiseForm && this.props.doInitialiseForm(fieldValues);
    }

    clearAllSelections() {
        // Use the existing onSelectAll method with false to unselect everything
        this.onSelectAll(false);
    }

    // Find parent document by child ID
    findParentDocumentByChildId(childId) {
        const documentList = this.props.documentList;
        if (!documentList) return null;

        for (const parentDoc of documentList) {
            if (parentDoc.childRows) {
                const childExists = parentDoc.childRows.some(child => child.id === childId);
                if (childExists) {
                    return parentDoc;
                }
            }
        }
        return null;
    }

    // Get all child IDs for a parent document
    getChildIds(parentId) {
        const documentList = this.props.documentList;
        if (!documentList) return [];

        const parentDoc = documentList.find(doc => doc.id === parentId);
        if (!parentDoc || !parentDoc.childRows) return [];

        return parentDoc.childRows.map(child => child.id);
    }

    // Check if all children of a parent are selected
    areAllChildrenSelected(parentId) {
        const fieldValues = this.props.formData && this.props.formData.fieldValues;
        if (!fieldValues) return false;

        const childIds = this.getChildIds(parentId);
        if (childIds.length === 0) return false;

        return childIds.every(childId => fieldValues[childId + '']);
    }

    onSelectDocumentFactory(documentId) {
        return (select, id = documentId, isParent = false) => {
            // If deselecting any document, immediately uncheck the "Select All" checkbox
            if (!select && this.props.formData && this.props.formData.fieldValues &&
                this.props.formData.fieldValues['_all']) {
                this.props.onFieldChange && this.props.onFieldChange('_all' + '', false);
            }

            // If this is a parent row with children, select/deselect all children
            if (isParent || this.getChildIds(documentId).length > 0) {
                // Update the parent checkbox
                this.props.onFieldChange && this.props.onFieldChange(documentId + '', select);

                // Update all child checkboxes
                const childIds = this.getChildIds(documentId);
                childIds.forEach(childId => {
                    this.props.onFieldChange && this.props.onFieldChange(childId + '', select);
                });
            }
            // If this is a child row
            else {
                // Update the child checkbox
                this.props.onFieldChange && this.props.onFieldChange(documentId + '', select);

                // Find the parent document
                const parentDoc = this.findParentDocumentByChildId(documentId);
                if (parentDoc) {
                    // If selecting a child and all siblings are now selected, select the parent
                    // If deselecting a child, deselect the parent
                    const fieldValues = this.props.formData && this.props.formData.fieldValues;
                    if (fieldValues) {
                        const childIds = this.getChildIds(parentDoc.id);
                        const allChildrenSelected = select &&
                            childIds.every(childId => {
                                return childId === documentId ? true : fieldValues[childId + ''];
                            });

                        // Update parent checkbox based on children's state
                        this.props.onFieldChange && this.props.onFieldChange(parentDoc.id + '', allChildrenSelected);
                    }
                }
            }
        };
    }

    onSelectDocumentDetailFactory(documentId) {
        return (event) => {
            log.debug('onSelectDocumentDetailFactory(%s): ', documentId);
            if (this.props.loadDocumentDetails) {
                this.props.loadDocumentDetails(documentId);
                this.setState({
                    documentDetailsOpen: true
                });
            } else {
                // For demo purposes when loadDocumentDetails is not provided
                console.log(`Document details requested for ID: ${documentId}`);
            }
        };
    }

    onCloseDocumentDetails() {
        log.debug('onCloseDocumentDetails: ');
        if (this.props.UnloadDocumentDetails) {
            this.props.UnloadDocumentDetails();
        }
        this.setState({
            documentDetailsOpen: false
        });
    }

    onRequestActionFactory(action) {
        return (evt) => {
            log.clearAllDictionaryErrors && log.clearAllDictionaryErrors();
            const documentList = this.props.documentList;
            const fieldValues = this.props.formData && this.props.formData.fieldValues;
            log.debug('onRequestActionFactory(%s): ', action, documentList, fieldValues);
            const documentIdsToAction = this.getSelectedIds();
            if (this.props.onRequestAction) {
                this.props.onRequestAction(action, documentIdsToAction);
            } else {
                // For demo purposes when onRequestAction is not provided
                console.log(`Action ${action} requested for documents:`, documentIdsToAction);
            }

            this.props.doFormSubmitted && this.props.doFormSubmitted();
            this.clearAllSelections();
        }
    }

    getSelectedIds() {
        const documentList = this.props.documentList;
        const fieldValues = this.props.formData && this.props.formData.fieldValues;

        if (!fieldValues) return [];

        const documentIdsToAction = [];

        // Process parent documents
        _.each(documentList, (document) => {
            // Process child documents
            if (document.childRows) {
                _.each(document.childRows, (childDoc) => {
                    if (fieldValues[childDoc.id + '']) {
                        documentIdsToAction.push({ id: childDoc.id, fileName: childDoc.fileName });
                    }
                });
            }
        });

        return documentIdsToAction;
    }

    render() {
        log.debug('render(): ', this.props);
        const documentList = this.props.documentList;
        const fieldValues = this.props.formData && this.props.formData.fieldValues;
        const selectedCount = (fieldValues && this.getSelectedIds().length) || 0;

        return (
            <ReadOnlyWrapper>
                <div>
                    {
                        (() => {
                            if (!documentList || documentList.length <= 0) {
                                return (
                                    <NoData
                                        noDataText="processedDocuments_NoData"
                                    />
                                );
                            }

                            if (!documentList || documentList.length > 0) {
                                return (
                                    <div>
                                        <table className="word-break-all document-table processed-documents-table">
                                            <colgroup>
                                                <col className="expander-column" />
                                                <col className="filename-column" />
                                                <col className="date-column" />
                                                <col className="code-column" />
                                                <col className="name-column" />
                                                <col className="count-column" />
                                                <col className="count-column" />
                                                <col className="count-column" />
                                                <col className="count-column" />
                                                <col className="count-column" />
                                                <col className="checkbox-column" />
                                            </colgroup>
                                            <thead>
                                                <DocumentItem
                                                    header={true}
                                                    formInitialising={!fieldValues}
                                                    onSelect={this.onSelectAll.bind(this)}
                                                    selected={fieldValues && fieldValues['_all']}
                                                    hasExpandableRows={true}
                                                />
                                            </thead>
                                            <tbody>
                                                {
                                                    documentList.map((document, index) => {
                                                        if (document.id === null) {
                                                            return (
                                                                <DocumentItem
                                                                    key={index}
                                                                    formInitialising={!fieldValues}
                                                                    document={document}
                                                                />
                                                            );
                                                        }

                                                        // Determine if this document has child rows
                                                        const hasChildRows = document.childRows && document.childRows.length > 0;

                                                        return (
                                                            <React.Fragment key={index}>
                                                                <DocumentItem
                                                                    formInitialising={!fieldValues}
                                                                    document={document}
                                                                    onSelect={this.onSelectDocumentFactory(document.id).bind(this)}
                                                                    selected={fieldValues && fieldValues[document.id + '']}
                                                                    loadDocumentDetails={this.onSelectDocumentDetailFactory(document.id).bind(this)}
                                                                    hasChildRows={hasChildRows}
                                                                    isExpanded={this.isRowExpanded(document.id)}
                                                                    onToggleExpand={hasChildRows ? this.toggleRowExpanded(document.id) : null}
                                                                />
                                                                {this.renderChildRows(document)}
                                                            </React.Fragment>
                                                        );
                                                    })
                                                }
                                            </tbody>
                                        </table>
                                        <div>
                                            <ApplicationMessages messageIds={[ALL_MESSAGES.FILE_OPERATION_STARTED_SUCCESS]} messageType="applicationSuccess" />
                                            <ApplicationMessages messageIds={[ALL_MESSAGES.FILE_OPERATION_ERROR]} messageType="applicationError" />
                                        </div>
                                        <div>
                                            <button
                                                disabled={selectedCount === 0 ? 'disabled' : ''}
                                                className={'secondary mt20'}
                                                onClick={this.onRequestActionFactory('export').bind(this)}
                                                excludeFromReadonly={true}
                                            >
                                                <LanguageStringResolver string="documentDetails_exportSelectedDocuments" />
                                            </button>
                                            <button
                                                disabled={selectedCount === 0 ? 'disabled' : ''}
                                                className={'secondary mt20'}
                                                onClick={this.onRequestActionFactory('delete').bind(this)}
                                            >
                                                <LanguageStringResolver string="documentDetails_deleteSelectedDocuments" />
                                            </button>
                                            <button
                                                disabled={selectedCount === 0 ? 'disabled' : ''}
                                                className={'secondary mt20'}
                                                onClick={this.onRequestActionFactory('redeliver').bind(this)}
                                            >
                                                <LanguageStringResolver string="documentDetails_redeliverSelectedDocuments" />
                                            </button>
                                        </div>
                                        <hr />
                                        <div>
                                            <DocumentDetails
                                                documentDetails={this.props.documentDetails}
                                                onClose={this.onCloseDocumentDetails.bind(this)}
                                                open={this.state.documentDetailsOpen} />
                                        </div>
                                    </div>
                                );
                            }
                        })()
                    }
                </div>
            </ReadOnlyWrapper>
        );
    }

    componentWillUnmount() {
        log.debug('componentWillUnmount(): ', this.props);
        this.props.doDestroyForm && this.props.doDestroyForm();
    }

    UNSAFE_componentWillMount() {
        log.debug('componentWillMount(): ', this.props);
        this.onSelectAll(this.props.initialSelection);
    }
}


DocumentListForm.defaultProps = {
    languages: {},
    initialSelection: false,
    onRequestAction: null,
    loadDocumentDetails: null,
    UnloadDocumentDetails: null
};

function mapStateToProps(state, props) {
    log.debug('mapStateToProps(): state & props', state, props);
    return {
        formData: (state.managedUxForms && state.managedUxForms.forms[FORM_NAME]),
        languages: state.languages || {},
    };
}

function mapDispatchersToProps(dispatch) {
    return {
        doInitialiseForm: (initialValues) => {
            dispatch && dispatch(MANAGED_UX_FORMS_CONTROLLER.doInitialiseForm(FORM_NAME, initialValues));
        },
        doResetForm: () => {
            dispatch && dispatch(MANAGED_UX_FORMS_CONTROLLER.doResetFormFieldsToInitialValues(FORM_NAME));
        },
        doClearForm: () => {
            dispatch && dispatch(MANAGED_UX_FORMS_CONTROLLER.doClearFormFields(FORM_NAME));
        },
        onFieldChange: (fieldName, value) => {
            dispatch && dispatch(MANAGED_UX_FORMS_CONTROLLER.doFormFieldChange(FORM_NAME, fieldName, value));
        },
        doFormSubmitted: () => {
            dispatch && dispatch(MANAGED_UX_FORMS_CONTROLLER.doFormSubmitted(FORM_NAME));
        },
        doDestroyForm: () => {
            dispatch && dispatch(MANAGED_UX_FORMS_CONTROLLER.doRemoveForm(FORM_NAME));
        }
    }
}

export default connect(mapStateToProps, mapDispatchersToProps)(DocumentListForm);